/**
 * /accounts/:id/buckets/:bucket — object browser.
 *
 * Hierarchical listing scoped by `?prefix=` query param. Folders click into
 * the next level; files expose download / share / rename / delete actions.
 * Drag-drop + file picker upload via the multipart engine. Bulk delete via
 * row checkboxes.
 */

import { type DragEvent, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { type ApiError, api } from '../lib/api';
import { formatBytes, formatDate } from '../lib/format';
import { useUploads } from '../upload/UploadQueue';
import { Badge, Banner, Icon } from '../components/brand';

interface S3Object {
  key: string;
  size: number;
  etag: string;
  lastModified: number | null;
  storageClass: string;
}

interface Folder {
  name: string;
}

interface ListResponse {
  prefix: string;
  folders: Folder[];
  objects: S3Object[];
  cursor: string | null;
}

interface BucketDetail {
  name: string;
  alias: string | null;
  publicBaseUrl: string | null;
  publicEnabled: boolean;
}

export function ObjectBrowser() {
  const { id = '', bucket = '' } = useParams<{ id: string; bucket: string }>();
  const navigate = useNavigate();
  const [search, setSearch] = useSearchParams();
  const prefix = search.get('prefix') ?? '';

  const qc = useQueryClient();
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const uploads = useUploads();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [dragOver, setDragOver] = useState(false);
  const completedKeysRef = useRef(0);

  const bucketQ = useQuery({
    queryKey: ['accounts', id, 'buckets', bucket],
    queryFn: () =>
      api.get<BucketDetail>(`/api/accounts/${id}/buckets/${encodeURIComponent(bucket)}`),
    enabled: !!id && !!bucket,
  });

  const list = useQuery({
    queryKey: ['objects', id, bucket, prefix],
    queryFn: () =>
      api.get<ListResponse>(
        `/api/accounts/${id}/buckets/${encodeURIComponent(bucket)}/objects?prefix=${encodeURIComponent(
          prefix,
        )}&delimiter=/`,
      ),
  });

  const refreshAll = () => {
    qc.invalidateQueries({ queryKey: ['objects', id, bucket, prefix] });
    setSelected(new Set());
  };

  const deleteSelected = useMutation({
    mutationFn: (keys: string[]) =>
      api.delete<{ deleted: string[]; errors: { key: string; code: string; message: string }[] }>(
        `/api/accounts/${id}/buckets/${encodeURIComponent(bucket)}/objects`,
        { keys },
      ),
    onSuccess: refreshAll,
  });

  const createFolder = useMutation({
    mutationFn: (name: string) =>
      api.post<{ path: string }>(
        `/api/accounts/${id}/buckets/${encodeURIComponent(bucket)}/folders`,
        { path: prefix + name + (name.endsWith('/') ? '' : '/') },
      ),
    onSuccess: refreshAll,
  });

  const downloadOne = useMutation({
    mutationFn: (key: string) =>
      api.post<{ url: string }>(
        `/api/accounts/${id}/buckets/${encodeURIComponent(bucket)}/objects/presign-get`,
        { key, expiresIn: 900, responseDisposition: 'attachment', filename: lastSegment(key) },
      ),
    onSuccess: ({ url }) => window.open(url, '_blank', 'noopener,noreferrer'),
  });

  const renameOne = useMutation({
    mutationFn: async ({ from, to }: { from: string; to: string }) => {
      await api.post(`/api/accounts/${id}/buckets/${encodeURIComponent(bucket)}/objects/copy`, {
        from,
        to,
      });
      await api.delete(`/api/accounts/${id}/buckets/${encodeURIComponent(bucket)}/objects`, {
        keys: [from],
      });
    },
    onSuccess: refreshAll,
  });

  const shareOne = useMutation({
    mutationFn: (key: string) =>
      api.post<{ url: string; kind: 'public' | 'presigned'; expiresAt?: number }>(
        `/api/accounts/${id}/buckets/${encodeURIComponent(bucket)}/objects/share`,
        { key, expiresIn: 86400, preferPublic: true },
      ),
    onSuccess: async (data) => {
      try {
        await navigator.clipboard.writeText(data.url);
        window.alert(
          data.kind === 'public'
            ? `Public URL copied:\n\n${data.url}`
            : `Presigned URL copied (expires ${data.expiresAt ? formatDate(data.expiresAt) : 'soon'}):\n\n${data.url}`,
        );
      } catch {
        window.prompt('Copy the URL:', data.url);
      }
    },
  });

  const crumbs = useMemo(() => crumbsFromPrefix(prefix), [prefix]);
  const bucketDetail = bucketQ.data;

  useEffect(() => {
    const doneNow = uploads.items.filter((it) => it.status === 'done').length;
    if (doneNow !== completedKeysRef.current) {
      completedKeysRef.current = doneNow;
      qc.invalidateQueries({ queryKey: ['objects', id, bucket, prefix] });
    }
  }, [uploads.items, qc, id, bucket, prefix]);

  function enqueueFiles(files: File[]) {
    if (files.length === 0 || !id || !bucket) return;
    uploads.enqueue(files, id, bucket, prefix);
  }

  function onPickFiles(e: React.ChangeEvent<HTMLInputElement>) {
    const files = e.target.files ? Array.from(e.target.files) : [];
    enqueueFiles(files);
    e.target.value = '';
  }

  function onDrop(e: DragEvent<HTMLDivElement>) {
    e.preventDefault();
    setDragOver(false);
    const files = e.dataTransfer.files ? Array.from(e.dataTransfer.files) : [];
    enqueueFiles(files);
  }

  function goTo(p: string) {
    if (p) search.set('prefix', p);
    else search.delete('prefix');
    setSearch(search);
    setSelected(new Set());
  }

  function toggle(key: string) {
    setSelected((s) => {
      const n = new Set(s);
      if (n.has(key)) n.delete(key);
      else n.add(key);
      return n;
    });
  }

  function toggleAll() {
    if (!list.data) return;
    if (selected.size === list.data.objects.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(list.data.objects.map((o) => o.key)));
    }
  }

  function handleNewFolder() {
    const name = window.prompt('Folder name:');
    if (!name) return;
    if (/[\\:*?"<>|]/.test(name)) {
      window.alert('Folder name contains invalid characters.');
      return;
    }
    createFolder.mutate(name);
  }

  function handleBulkDelete() {
    if (selected.size === 0) return;
    if (!window.confirm(`Delete ${selected.size} object(s)?`)) return;
    deleteSelected.mutate(Array.from(selected));
  }

  const objects = list.data?.objects ?? [];
  const folders = list.data?.folders ?? [];
  const empty = !list.isLoading && folders.length === 0 && objects.length === 0;
  const allSelected = objects.length > 0 && selected.size === objects.length;
  const bucketLabel = bucketDetail?.alias || bucket;

  return (
    <div
      data-screen-label="05 Object browser"
      onDragOver={(e) => {
        e.preventDefault();
        setDragOver(true);
      }}
      onDragLeave={(e) => {
        if (e.currentTarget === e.target) setDragOver(false);
      }}
      onDrop={onDrop}
    >
      <div style={{ marginBottom: 16 }}>
        <button
          type="button"
          className="bm-btn bm-btn-sm"
          style={{
            color: 'var(--bm-text-muted)',
            padding: '0 0 12px',
          }}
          onClick={() => navigate(`/accounts/${id}/buckets`)}
        >
          <Icon.arrowLeft size={12} /> Buckets
        </button>
        <div
          style={{
            display: 'flex',
            alignItems: 'flex-end',
            justifyContent: 'space-between',
            marginTop: 8,
          }}
        >
          <div>
            <div className="bm-row bm-gap-12" style={{ marginBottom: 8 }}>
              <Badge variant="neutral">bucket</Badge>
              {bucketDetail?.publicEnabled && <Badge variant="success">public</Badge>}
              <span className="mono" style={{ fontSize: 12, color: 'var(--bm-text-muted)' }}>
                {bucket}
              </span>
            </div>
            <h1 className="h-page">{bucketLabel}</h1>
          </div>
        </div>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
        <nav className="bm-breadcrumb">
          <button
            type="button"
            className="bm-breadcrumb-seg"
            data-current={!prefix}
            onClick={() => goTo('')}
          >
            root
          </button>
          {crumbs.map((c, i) => (
            <span key={c.path} style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              <Icon.chevronRight size={12} style={{ color: 'var(--bm-text-faint)' }} />
              <button
                type="button"
                className="bm-breadcrumb-seg"
                data-current={i === crumbs.length - 1}
                onClick={() => goTo(c.path)}
              >
                {c.label}
              </button>
            </span>
          ))}
        </nav>
      </div>

      <div className="bm-toolbar">
        <button
          type="button"
          className="bm-btn bm-btn-primary bm-btn-sm"
          onClick={() => fileInputRef.current?.click()}
        >
          <Icon.upload size={12} /> Upload
        </button>
        <input ref={fileInputRef} type="file" multiple hidden onChange={onPickFiles} />
        <button
          type="button"
          className="bm-btn bm-btn-ghost bm-btn-sm"
          disabled={createFolder.isPending}
          onClick={handleNewFolder}
        >
          <Icon.plus size={12} /> {createFolder.isPending ? 'Creating…' : 'Folder'}
        </button>
        <button
          type="button"
          className="bm-btn bm-btn-danger bm-btn-sm"
          disabled={selected.size === 0 || deleteSelected.isPending}
          onClick={handleBulkDelete}
          style={{ opacity: selected.size === 0 ? 0.45 : 1 }}
        >
          <Icon.trash size={12} />{' '}
          {deleteSelected.isPending
            ? 'Deleting…'
            : `Delete${selected.size ? ` ${selected.size}` : ''}`}
        </button>

        <span className="bm-spacer" />

        <button
          type="button"
          className="bm-btn bm-btn-icon bm-btn-sm"
          title="Refresh"
          onClick={refreshAll}
        >
          <Icon.refresh size={13} />
        </button>
      </div>

      {list.error && (
        <div style={{ marginBottom: 12 }}>
          <Banner variant="danger" code={(list.error as ApiError).code ?? 'ERROR'}>
            {(list.error as Error).message}
          </Banner>
        </div>
      )}
      {createFolder.error && (
        <div style={{ marginBottom: 12 }}>
          <Banner variant="danger" code={(createFolder.error as ApiError).code ?? 'ERROR'}>
            {(createFolder.error as Error).message}
          </Banner>
        </div>
      )}
      {deleteSelected.error && (
        <div style={{ marginBottom: 12 }}>
          <Banner variant="danger" code={(deleteSelected.error as ApiError).code ?? 'ERROR'}>
            {(deleteSelected.error as Error).message}
          </Banner>
        </div>
      )}

      {empty ? (
        <div className="bm-card bm-empty">
          <div
            style={{
              width: 56,
              height: 56,
              borderRadius: 999,
              background: 'var(--bm-tint-2)',
              color: 'var(--bm-text-muted)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Icon.bucket size={22} />
          </div>
          <h3>{dragOver ? 'Drop to upload' : 'Empty prefix'}</h3>
          <p className="muted" style={{ fontSize: 13, maxWidth: 320 }}>
            Drop files anywhere on the page to upload — or use the button above.
          </p>
          <button
            type="button"
            className="bm-btn bm-btn-primary bm-btn-sm"
            onClick={() => fileInputRef.current?.click()}
          >
            <Icon.upload size={12} /> Upload
          </button>
        </div>
      ) : (
        <div className="bm-card-flush bm-browser-table">
          <table className="bm-table">
            <thead>
              <tr>
                <th style={{ width: 36, paddingLeft: 16 }}>
                  <Checkbox checked={allSelected} onChange={toggleAll} />
                </th>
                <th style={{ width: 28 }} />
                <th>Name</th>
                <th style={{ width: 120, textAlign: 'right' }}>Size</th>
                <th style={{ width: 170, textAlign: 'right' }}>Modified</th>
                <th style={{ width: 200, textAlign: 'right', paddingRight: 16 }} />
              </tr>
            </thead>
            <tbody>
              {list.isLoading && (
                <tr>
                  <td
                    colSpan={6}
                    style={{ padding: 24, textAlign: 'center', color: 'var(--bm-text-muted)' }}
                  >
                    Loading…
                  </td>
                </tr>
              )}
              {folders.map((f) => (
                <tr key={`folder:${f.name}`}>
                  <td style={{ paddingLeft: 16 }} />
                  <td style={{ color: 'var(--bm-accent)' }}>
                    <Icon.folder size={14} />
                  </td>
                  <td>
                    <button
                      type="button"
                      className="mono"
                      style={{
                        background: 'transparent',
                        border: 0,
                        padding: 0,
                        font: 'inherit',
                        cursor: 'pointer',
                        fontSize: 12.5,
                        color: 'var(--bm-text)',
                      }}
                      onClick={() => goTo(f.name)}
                    >
                      {trimPrefix(f.name, prefix)}/
                    </button>
                  </td>
                  <td
                    className="mono tnum"
                    style={{
                      textAlign: 'right',
                      fontSize: 12,
                      color: 'var(--bm-text-muted)',
                    }}
                  >
                    —
                  </td>
                  <td
                    className="mono tnum"
                    style={{
                      textAlign: 'right',
                      fontSize: 12,
                      color: 'var(--bm-text-muted)',
                    }}
                  >
                    —
                  </td>
                  <td style={{ textAlign: 'right', paddingRight: 16 }} />
                </tr>
              ))}
              {objects.map((o) => {
                const name = lastSegment(o.key);
                const isSelected = selected.has(o.key);
                return (
                  <tr key={o.key} data-selected={isSelected}>
                    <td style={{ paddingLeft: 16 }}>
                      <Checkbox checked={isSelected} onChange={() => toggle(o.key)} />
                    </td>
                    <td style={{ color: 'var(--bm-text-muted)' }}>
                      <Icon.file size={14} />
                    </td>
                    <td>
                      <span
                        className="mono"
                        title={o.key}
                        style={{ fontSize: 12.5, color: 'var(--bm-text)' }}
                      >
                        {name}
                      </span>
                    </td>
                    <td
                      className="mono tnum"
                      style={{
                        textAlign: 'right',
                        fontSize: 12,
                        color: 'var(--bm-text-muted)',
                      }}
                    >
                      {formatBytes(o.size)}
                    </td>
                    <td
                      className="mono tnum"
                      style={{
                        textAlign: 'right',
                        fontSize: 12,
                        color: 'var(--bm-text-muted)',
                      }}
                    >
                      {formatDate(o.lastModified)}
                    </td>
                    <td style={{ textAlign: 'right', paddingRight: 16 }}>
                      <div
                        className="bm-row bm-gap-4"
                        style={{ justifyContent: 'flex-end', opacity: 0.78 }}
                      >
                        <button
                          type="button"
                          className="bm-btn bm-btn-sm"
                          title="Download"
                          disabled={downloadOne.isPending}
                          onClick={() => downloadOne.mutate(o.key)}
                        >
                          <Icon.download size={12} />
                        </button>
                        <button
                          type="button"
                          className="bm-btn bm-btn-sm"
                          title="Share"
                          disabled={shareOne.isPending}
                          onClick={() => shareOne.mutate(o.key)}
                        >
                          <Icon.share size={12} />
                        </button>
                        <button
                          type="button"
                          className="bm-btn bm-btn-sm"
                          title="Rename"
                          disabled={renameOne.isPending}
                          onClick={() => {
                            const next = window.prompt('Rename to:', name);
                            if (!next || next === name) return;
                            if (/\//.test(next)) {
                              window.alert(
                                'Use a name without "/" — the new key keeps the current folder.',
                              );
                              return;
                            }
                            const to = prefix + next;
                            renameOne.mutate({ from: o.key, to });
                          }}
                        >
                          <Icon.edit size={12} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {list.data?.cursor && (
        <div className="bm-row" style={{ marginTop: 12, color: 'var(--bm-text-muted)' }}>
          <span className="muted" style={{ fontSize: 12 }}>
            More results available — paginated continuation lands in a future commit.
          </span>
        </div>
      )}

      {selected.size > 0 && (
        <div className="bm-selection-bar">
          <span className="mono tnum" style={{ fontSize: 12, color: 'var(--bm-text)' }}>
            {selected.size} selected
          </span>
          <span className="bm-spacer" />
          <button
            type="button"
            className="bm-btn bm-btn-danger bm-btn-sm"
            disabled={deleteSelected.isPending}
            onClick={handleBulkDelete}
          >
            <Icon.trash size={12} /> Delete
          </button>
          <button
            type="button"
            className="bm-btn bm-btn-sm"
            style={{ color: 'var(--bm-text-muted)' }}
            onClick={() => setSelected(new Set())}
          >
            <Icon.x size={11} />
          </button>
        </div>
      )}
    </div>
  );
}

/* ─── Helpers ─────────────────────────────────────────────────────── */

function Checkbox({ checked, onChange }: { checked: boolean; onChange: () => void }) {
  return (
    <button
      type="button"
      className="bm-checkbox"
      data-checked={checked}
      onClick={(e) => {
        e.stopPropagation();
        onChange();
      }}
    >
      {checked && <Icon.check size={10} />}
    </button>
  );
}

function crumbsFromPrefix(prefix: string): { label: string; path: string }[] {
  if (!prefix) return [];
  const parts = prefix.replace(/\/$/, '').split('/');
  const out: { label: string; path: string }[] = [];
  let acc = '';
  for (const part of parts) {
    acc += `${part}/`;
    out.push({ label: part, path: acc });
  }
  return out;
}

function lastSegment(key: string): string {
  const trimmed = key.replace(/\/$/, '');
  const i = trimmed.lastIndexOf('/');
  return i < 0 ? trimmed : trimmed.slice(i + 1);
}

function trimPrefix(folderName: string, prefix: string): string {
  if (folderName.startsWith(prefix)) return folderName.slice(prefix.length).replace(/\/$/, '');
  return folderName.replace(/\/$/, '');
}
